// frontend/src/components/WorksIntelligenceCard.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-03-11
// Updated:      2026-09-16
//
// Description:  Reusable works intelligence card for location-aware and job-aware UI surfaces.
//               Fetches tenant-safe Street Manager works intelligence from the backend
//               using either:
//                 • a job id, or
//                 • pinned lat/lon with explicit time window
//               Intended for Job edit/create flows and dashboard/widget reuse.
//
// Notes:
//               - Intentionally separate from Traffic Intelligence (TomTom live traffic).
//               - Supports both job-based and location-based works intelligence routes.
//               - Surfaces operational works risk first, then top disruptions.
//               - Compact, mobile-friendly, no bulky nested panels.
//               - Updated to make works start/end/duration much more obvious in the UI.
//               - For job-aware surfaces, valid jobId takes precedence so the backend can
//                 derive the window from job_resource_assignments.
//               - Location mode is now a fallback only when no valid jobId exists.
//               - In location mode, the card waits quietly for both windowStart and
//                 windowEnd instead of surfacing a false error while state is settling.
//
// Responsive refinement (2026-09-16):
//               - Mobile removes card-inside-card framing and presents one flat works surface.
//               - Desktop retains the existing bordered outer/inner card hierarchy.
//               - Primary issue and disruption rows become divider sections on mobile.
//               - Time and risk metrics use mobile grids instead of fixed minimum widths.
//               - Long roads, authorities, event details, chips and technical footer wrap safely.
//               - Works API mode selection, debounce, persistence and refresh behaviour are unchanged.

import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";

const isFiniteNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n);
};

const safeText = (v, fallback = "—") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s || fallback;
};

const formatDateTimeGB = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/London",
    });
  } catch {
    return "—";
  }
};

const formatDistance = (km, m) => {
  if (isFiniteNumber(km)) {
    const n = Number(km);
    if (n < 1) {
      if (isFiniteNumber(m)) return `${Math.round(Number(m))} m`;
      return `${n.toFixed(3)} km`;
    }
    return `${n.toFixed(2)} km`;
  }
  if (isFiniteNumber(m)) return `${Math.round(Number(m))} m`;
  return "—";
};

const formatDuration = (startAt, endAt) => {
  if (!startAt || !endAt) return "—";
  try {
    const start = new Date(startAt);
    const end = new Date(endAt);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "—";

    let diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return "—";

    const totalMinutes = Math.round(diffMs / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const mins = totalMinutes % 60;

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);

    return parts.length ? parts.join(" ") : "0m";
  } catch {
    return "—";
  }
};

const endsSoonTone = (endAt) => {
  if (!endAt) {
    return {
      bg: "rgba(107, 114, 128, 0.10)",
      border: "rgba(107, 114, 128, 0.22)",
      text: "#4b5563",
      label: "End unknown",
    };
  }

  try {
    const now = new Date();
    const end = new Date(endAt);
    if (Number.isNaN(end.getTime())) {
      return {
        bg: "rgba(107, 114, 128, 0.10)",
        border: "rgba(107, 114, 128, 0.22)",
        text: "#4b5563",
        label: "End unknown",
      };
    }

    const diffHours = (end.getTime() - now.getTime()) / 3600000;

    if (diffHours < 0) {
      return {
        bg: "rgba(107, 114, 128, 0.10)",
        border: "rgba(107, 114, 128, 0.22)",
        text: "#4b5563",
        label: "Ended",
      };
    }

    if (diffHours <= 24) {
      return {
        bg: "rgba(239, 68, 68, 0.12)",
        border: "rgba(239, 68, 68, 0.30)",
        text: "#b91c1c",
        label: "Ends soon",
      };
    }

    if (diffHours <= 72) {
      return {
        bg: "rgba(245, 158, 11, 0.12)",
        border: "rgba(245, 158, 11, 0.28)",
        text: "#b45309",
        label: "Ends this week",
      };
    }

    return {
      bg: "rgba(59, 130, 246, 0.10)",
      border: "rgba(59, 130, 246, 0.24)",
      text: "#1d4ed8",
      label: "Ongoing",
    };
  } catch {
    return {
      bg: "rgba(107, 114, 128, 0.10)",
      border: "rgba(107, 114, 128, 0.22)",
      text: "#4b5563",
      label: "End unknown",
    };
  }
};

const riskToneMap = {
  low: {
    bg: "rgba(16, 185, 129, 0.10)",
    border: "rgba(16, 185, 129, 0.30)",
    text: "#047857",
    label: "Low Risk",
  },
  moderate: {
    bg: "rgba(59, 130, 246, 0.10)",
    border: "rgba(59, 130, 246, 0.28)",
    text: "#1d4ed8",
    label: "Moderate Risk",
  },
  high: {
    bg: "rgba(245, 158, 11, 0.12)",
    border: "rgba(245, 158, 11, 0.30)",
    text: "#b45309",
    label: "High Risk",
  },
  severe: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.35)",
    text: "#b91c1c",
    label: "Severe Risk",
  },
  unknown: {
    bg: "rgba(107, 114, 128, 0.10)",
    border: "rgba(107, 114, 128, 0.25)",
    text: "#4b5563",
    label: "Unknown",
  },
};

const disruptionToneMap = {
  road_closure: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.35)",
    fg: "#b91c1c",
    label: "Road closure",
    icon: "closed",
  },
  lane_closure: {
    bg: "rgba(245, 158, 11, 0.12)",
    border: "rgba(245, 158, 11, 0.30)",
    fg: "#b45309",
    label: "Lane closure",
    icon: "lane_closed",
  },
  multi_way_signals: {
    bg: "rgba(59, 130, 246, 0.10)",
    border: "rgba(59, 130, 246, 0.24)",
    fg: "#1d4ed8",
    label: "Signals",
    icon: "signals",
  },
  section58: {
    bg: "rgba(107, 114, 128, 0.10)",
    border: "rgba(107, 114, 128, 0.22)",
    fg: "#4b5563",
    label: "Section 58",
    icon: "restriction",
  },
  default: {
    bg: "rgba(107, 114, 128, 0.10)",
    border: "rgba(107, 114, 128, 0.22)",
    fg: "#4b5563",
    label: "Works activity",
    icon: "works",
  },
};

const chipStyle = (tone) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 10px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 700,
  background: tone?.bg || "rgba(107, 114, 128, 0.10)",
  border: `1px solid ${tone?.border || "rgba(107, 114, 128, 0.25)"}`,
  color: tone?.text || "#4b5563",
});

const secondaryChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 10px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 700,
  background: "rgba(17, 24, 39, 0.05)",
  border: "1px solid rgba(17, 24, 39, 0.10)",
  color: "#111827",
};

const metricBlockStyle = {
  minWidth: 0,
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 12px",
  background: "#ffffff",
};

const timeMetricBlockStyle = {
  minWidth: 0,
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: "12px 14px",
  background: "#ffffff",
};

const DisruptionSvgIcon = ({ type = "works", fg = "#4b5563", size = 30 }) => {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
  };

  if (type === "closed") {
    return (
      <svg {...common}>
        <rect x="4" y="9" width="24" height="14" rx="3" stroke={fg} strokeWidth="2" />
        <path d="M10 14L22 18" stroke={fg} strokeWidth="2.5" strokeLinecap="round" />
        <path d="M22 14L10 18" stroke={fg} strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === "lane_closed") {
    return (
      <svg {...common}>
        <path d="M8 25L12 7" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M20 25L24 7" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M15.5 24L18.5 8" stroke={fg} strokeWidth="2" strokeLinecap="round" strokeDasharray="2.5 3" />
        <path d="M22.5 10L27 14.5L22.5 19" stroke={fg} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === "signals") {
    return (
      <svg {...common}>
        <rect x="11" y="6" width="10" height="18" rx="4" stroke={fg} strokeWidth="2" />
        <circle cx="16" cy="11" r="1.7" fill={fg} opacity="1" />
        <circle cx="16" cy="15" r="1.7" fill={fg} opacity="0.7" />
        <circle cx="16" cy="19" r="1.7" fill={fg} opacity="0.4" />
        <path d="M16 24V28" stroke={fg} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === "restriction") {
    return (
      <svg {...common}>
        <circle cx="16" cy="16" r="11" stroke={fg} strokeWidth="2" />
        <path d="M10 16H22" stroke={fg} strokeWidth="2.4" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M7 24L11 8" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
      <path d="M21 24L25 8" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
      <path d="M14 24L18 8" stroke={fg} strokeWidth="2" strokeLinecap="round" strokeDasharray="2.5 3" />
      <path d="M9 18H23" stroke={fg} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
};

const getDisruptionTone = (item) => {
  const tm = String(item?.traffic_management_type || "").trim().toLowerCase();
  const topic = String(item?.source_topic || "").trim().toLowerCase();

  if (topic === "section58") return disruptionToneMap.section58;
  if (tm === "road_closure") return disruptionToneMap.road_closure;
  if (tm === "lane_closure") return disruptionToneMap.lane_closure;
  if (tm === "multi_way_signals") return disruptionToneMap.multi_way_signals;
  return disruptionToneMap.default;
};

const formatApiError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (data == null) return status ? `${status} — Request failed` : err?.message || "Request failed";
  if (typeof data === "string") return status ? `${status} — ${data}` : data;

  const detail = data?.detail ?? data?.message ?? data;
  try {
    const text = typeof detail === "string" ? detail : JSON.stringify(detail);
    return status ? `${status} — ${text}` : text;
  } catch {
    return status ? `${status} — Error` : "Error";
  }
};

const WorksIntelligenceCard = ({
  title = "Works Intelligence",
  jobId = null,
  lat = null,
  lon = null,
  locationLabel = "",
  persist = true,
  className = "",
  showRefresh = true,
  autoFetch = true,
  showTechnicalFooter = false,
  radiusM = 5000,
  limit = 20,
  windowStart = "",
  windowEnd = "",
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const debounceRef = useRef(null);
  const requestKeyRef = useRef("");

  const hasJob = Number.isInteger(Number(jobId)) && Number(jobId) > 0;
  const hasCoords = isFiniteNumber(lat) && isFiniteNumber(lon);

  // Correct mode selection:
  // - Prefer job mode when a valid job id exists so the backend can derive the window
  //   from job_resource_assignments.
  // - Use location mode only as fallback when there is no job context available.
  const isJobMode = hasJob;
  const isLocationMode = !isJobMode && hasCoords;
  const locationWindowReady = Boolean(windowStart && windowEnd);
  const canFetch = isJobMode || (isLocationMode && locationWindowReady);

  const loadWorks = async () => {
    if (!canFetch) {
      setData(null);
      setErr("");
      setLoading(false);
      return;
    }

    const requestKey = [
      isJobMode ? `job:${jobId}` : `loc:${Number(lat)}:${Number(lon)}`,
      radiusM,
      limit,
      windowStart || "",
      windowEnd || "",
      persist ? "1" : "0",
    ].join("|");

    requestKeyRef.current = requestKey;
    setLoading(true);
    setErr("");

    try {
      const params = {
        radius_m: radiusM,
        limit,
      };

      if (isLocationMode) {
        params.window_start = windowStart;
        params.window_end = windowEnd;
      } else {
        if (windowStart) params.window_start = windowStart;
        if (windowEnd) params.window_end = windowEnd;
      }

      let res;

      if (isJobMode) {
        res = await axiosInstance.get(`/jobs/${jobId}/works-intelligence`, { params });
      } else {
        res = await axiosInstance.get("/works-intelligence", {
          params: {
            lat: Number(lat),
            lon: Number(lon),
            ...params,
          },
        });
      }

      if (requestKeyRef.current !== requestKey) return;
      setData(res?.data || null);
    } catch (e) {
      if (requestKeyRef.current !== requestKey) return;
      const msg = formatApiError(e);
      setErr(msg);
      setData(null);
    } finally {
      if (requestKeyRef.current === requestKey) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!autoFetch) return undefined;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!hasJob && !hasCoords) {
      setData(null);
      setErr("");
      setLoading(false);
      return undefined;
    }

    if (isLocationMode && !locationWindowReady) {
      setData(null);
      setErr("");
      setLoading(false);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      loadWorks();
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoFetch,
    hasJob,
    hasCoords,
    isJobMode,
    isLocationMode,
    locationWindowReady,
    jobId,
    lat,
    lon,
    radiusM,
    limit,
    windowStart,
    windowEnd,
    persist,
  ]);

  const summary = data?.summary || null;
  const disruptions = Array.isArray(data?.disruptions) ? data.disruptions : [];
  const topRisk = Array.isArray(data?.top_risks) && data.top_risks.length ? data.top_risks[0] : disruptions[0] || null;
  const riskBandKey = String(summary?.risk_band || "unknown").trim().toLowerCase();
  const riskTone = riskToneMap[riskBandKey] || riskToneMap.unknown;
  const primaryTone = getDisruptionTone(topRisk);
  const endTone = endsSoonTone(topRisk?.end_at);

  const resolvedWindow = data?.job_window || data?.window || null;

  return (
    <div className={`mt-3 min-w-0 bg-transparent sm:mt-4 sm:rounded-xl sm:border sm:border-gray-200 sm:bg-white ${className}`}>
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3 px-0 py-2 sm:px-4 sm:py-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold" style={{ color: "var(--accent-color)" }}>
            {title}
          </div>
          <div className="break-words text-xs text-gray-600">
            {isJobMode
              ? `Operational street works impact for job ${jobId}${locationLabel ? ` · ${locationLabel}` : ""}`
              : isLocationMode
              ? `Operational street works impact for pinned location${locationLabel ? ` · ${locationLabel}` : ""}`
              : "Provide a valid job id or pinned location to enable works intelligence."}
          </div>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {summary ? (
            <span style={chipStyle(riskTone)}>{safeText(riskTone?.label, "Risk")}</span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
              No intelligence
            </span>
          )}

          {showRefresh && canFetch && (
            <button
              type="button"
              onClick={loadWorks}
              disabled={loading}
              className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick disabled:opacity-60 text-white text-xs font-semibold px-3 py-1 rounded shadow"
              title="Refresh works intelligence"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          )}
        </div>
      </div>

      <div className="min-w-0 px-0 pb-2 sm:px-4 sm:pb-4">
        {!hasJob && !hasCoords ? (
          <div className="text-xs text-gray-600 italic">No valid job or location context available yet.</div>
        ) : isLocationMode && !locationWindowReady ? (
          <div className="text-xs text-gray-600 italic">Set both start and end times to resolve location-based works intelligence.</div>
        ) : loading && !data ? (
          <div className="text-xs text-gray-600 italic">Loading works intelligence…</div>
        ) : err ? (
          <div className="text-xs text-red-600">❌ {err}</div>
        ) : !data || !summary ? (
          <div className="text-xs text-gray-600 italic">No works intelligence available for this context.</div>
        ) : (
          <div className="min-w-0 bg-transparent py-2 sm:rounded-lg sm:border sm:border-gray-100 sm:bg-white sm:p-4">
            <div className="flex min-w-0 items-start gap-3">
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: 12,
                  background: primaryTone.bg,
                  border: `1px solid ${primaryTone.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <DisruptionSvgIcon type={primaryTone.icon} fg={primaryTone.fg} size={30} />
              </div>

              <div className="min-w-0 flex-1">
                <div
                  style={{
                    fontSize: 13,
                    lineHeight: 1.45,
                    fontWeight: 600,
                    color: "#111827",
                    overflowWrap: "anywhere",
                  }}
                >
                  {safeText(data?.narrative, "No works impact summary available.")}
                </div>

                <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
                  <span style={chipStyle(riskTone)}>{safeText(riskTone?.label, "Risk")}</span>
                  <span style={secondaryChipStyle}>
                    {summary?.count ?? 0} relevant disruption{Number(summary?.count || 0) === 1 ? "" : "s"}
                  </span>
                  {topRisk ? (
                    <span style={secondaryChipStyle}>{primaryTone.label}</span>
                  ) : (
                    <span style={secondaryChipStyle}>No surfaced works issue</span>
                  )}
                  {topRisk?.end_at ? <span style={chipStyle(endTone)}>{endTone.label}</span> : null}
                </div>
              </div>
            </div>

            {topRisk ? (
              <div className="mt-4 border-t border-gray-200 pt-3 sm:rounded-xl sm:border sm:border-gray-200 sm:bg-[#fafafa] sm:p-[14px]">
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Primary works issue
                    </div>
                    <div className="mt-1 break-words text-sm font-semibold leading-5 text-gray-900">
                      {safeText(topRisk?.street_name, "Unknown road")}
                    </div>
                    <div className="mt-1 break-words text-xs leading-5 text-gray-600">
                      {safeText(topRisk?.traffic_management_type, "Works activity").replaceAll("_", " ")}
                      {topRisk?.highway_authority ? ` · ${topRisk.highway_authority}` : ""}
                    </div>
                  </div>

                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span style={secondaryChipStyle}>
                      Distance {formatDistance(topRisk?.distance_km, topRisk?.distance_m)}
                    </span>
                    <span style={secondaryChipStyle}>Severity {safeText(topRisk?.severity_score, "—")}</span>
                    <span style={secondaryChipStyle}>Impact {safeText(topRisk?.impact_score, "—")}</span>
                  </div>
                </div>

                {topRisk?.event_details ? (
                  <div className="mt-2 break-words text-xs text-gray-600">{safeText(topRisk.event_details)}</div>
                ) : null}

                <div className="mt-4 grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
                  <div className="min-w-0 sm:min-w-[170px]" style={timeMetricBlockStyle}>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Starts</div>
                    <div className="mt-1 text-sm font-semibold text-gray-900">{formatDateTimeGB(topRisk?.start_at)}</div>
                  </div>

                  <div
                    className="min-w-0 sm:min-w-[170px]"
                    style={{
                      ...timeMetricBlockStyle,
                      border: `1px solid ${endTone.border}`,
                      background: endTone.bg,
                    }}
                  >
                    <div className="text-[11px] uppercase tracking-wide" style={{ color: endTone.text, opacity: 0.9 }}>
                      Ends
                    </div>
                    <div className="mt-1 text-sm font-semibold" style={{ color: endTone.text }}>
                      {formatDateTimeGB(topRisk?.end_at)}
                    </div>
                  </div>

                  <div className="min-w-0 sm:min-w-[170px]" style={timeMetricBlockStyle}>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Duration</div>
                    <div className="mt-1 text-sm font-semibold text-gray-900">
                      {formatDuration(topRisk?.start_at, topRisk?.end_at)}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-4 grid min-w-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3">
              <div className="min-w-0 sm:min-w-[120px]" style={metricBlockStyle}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Risk score</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{safeText(summary?.risk_score, "—")}</div>
              </div>

              <div className="min-w-0 sm:min-w-[120px]" style={metricBlockStyle}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Nearest</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">
                  {formatDistance(summary?.nearest_distance_km, summary?.nearest_distance_m)}
                </div>
              </div>

              <div className="min-w-0 sm:min-w-[120px]" style={metricBlockStyle}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Top severity</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{safeText(summary?.max_severity_score, "—")}</div>
              </div>

              <div className="min-w-0 sm:min-w-[120px]" style={metricBlockStyle}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Top impact</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{safeText(summary?.max_impact_score, "—")}</div>
              </div>

              <div className="min-w-0 sm:min-w-[120px]" style={metricBlockStyle}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Window source</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{safeText(resolvedWindow?.source, "—")}</div>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {disruptions.length > 0 ? (
                disruptions.slice(0, 4).map((item) => {
                  const itemEndTone = endsSoonTone(item?.end_at);
                  return (
                    <div
                      key={item.id}
                      className="border-t border-gray-200 py-3 first:border-t-0 sm:rounded-[10px] sm:border sm:border-black/5 sm:bg-[#fafafa] sm:px-3 sm:py-2.5"
                    >
                      <div className="break-words text-xs leading-5 text-gray-800">
                        <strong>{safeText(item?.traffic_management_type, "works").replaceAll("_", " ")}</strong> on{" "}
                        <strong>{safeText(item?.street_name, "unknown road")}</strong>
                        {item?.distance_km != null ? ` · ${formatDistance(item.distance_km, item.distance_m)}` : ""}
                        {item?.overlaps_job_window ? " · overlaps window" : ""}
                      </div>

                      <div className="mt-2 flex min-w-0 flex-wrap gap-2">
                        <span style={secondaryChipStyle}>Starts {formatDateTimeGB(item?.start_at)}</span>
                        <span style={chipStyle(itemEndTone)}>Ends {formatDateTimeGB(item?.end_at)}</span>
                        <span style={secondaryChipStyle}>Duration {formatDuration(item?.start_at, item?.end_at)}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="break-words text-xs leading-5 text-gray-700">
                  • No nearby active or overlapping street works were found for the resolved window.
                </div>
              )}
            </div>

            {showTechnicalFooter ? (
              <div className="mt-3 break-words text-[11px] leading-5 text-gray-500">
                Window {formatDateTimeGB(resolvedWindow?.start_at)} → {formatDateTimeGB(resolvedWindow?.end_at)}
                {data?.job_window?.assignment_count_used != null
                  ? ` · Assignments used ${data.job_window.assignment_count_used}`
                  : ""}
                {data?.query?.radius_m ? ` · Radius ${data.query.radius_m} m` : ""}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorksIntelligenceCard;